interface OpenAIResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
}

interface TradingStrategy {
  name: string;
  description: string;
  type: 'scalping' | 'swing' | 'long-term' | 'arbitrage' | 'grid';
  risk: 'Low' | 'Medium' | 'High';
  timeframe: string;
  expectedReturn: string;
  signals: string[];
  stopLoss: string;
  takeProfit: string;
}

class OpenAIService {
  private apiKey: string;
  private baseUrl = 'https://api.openai.com/v1/chat/completions';

  constructor() {
    // For Vite, we need to use import.meta.env with VITE_ prefix
    this.apiKey = import.meta.env.VITE_OPENAI_API_KEY || '';
    console.log('OpenAI Service initialized, API Key:', this.apiKey ? 'Present' : 'Not found');
    if (!this.apiKey) {
      console.warn('OpenAI API key not found. Please add VITE_OPENAI_API_KEY to your .env file. AI features will be limited.');
    }
  }

  async generateResponse(prompt: string): Promise<string> {
    console.log('generateResponse called with prompt:', prompt);
    console.log('API Key status:', this.apiKey ? 'Present' : 'Not found');
    console.log('API Key value:', this.apiKey);
    
    if (!this.apiKey) {
      console.log('Using demo response for prompt:', prompt);
      const demoResponse = this.generateDemoResponse(prompt);
      console.log('Demo response generated:', demoResponse.substring(0, 100) + '...');
      return demoResponse;
    }

    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: this.buildSystemPrompt()
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 1000,
          temperature: 0.7,
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data: OpenAIResponse = await response.json();
      
      if (data.choices && data.choices[0]?.message?.content) {
        return data.choices[0].message.content;
      } else {
        throw new Error('Invalid response format from OpenAI API');
      }
    } catch (error) {
      console.error('Error calling OpenAI API:', error);
      return "I'm experiencing technical difficulties. Please try again later.";
    }
  }

  async generateTradingStrategy(
    symbol: string, 
    marketCondition: string, 
    riskTolerance: string,
    timeframe: string
  ): Promise<TradingStrategy> {
    const prompt = `Generate a detailed trading strategy for ${symbol} given:
    - Market Condition: ${marketCondition}
    - Risk Tolerance: ${riskTolerance}
    - Timeframe: ${timeframe}
    
    Please provide a comprehensive strategy with specific entry/exit signals, risk management, and expected returns.`;

    try {
      const response = await this.generateResponse(prompt);
      return this.parseStrategyResponse(response, symbol);
    } catch (error) {
      console.error('Error generating strategy:', error);
      return this.getDefaultStrategy(symbol);
    }
  }

  async analyzeMarket(symbols: string[]): Promise<string> {
    const prompt = `Provide a comprehensive market analysis for the following trading pairs: ${symbols.join(', ')}.
    
    Include:
    - Current market sentiment
    - Key support and resistance levels
    - Potential trading opportunities
    - Risk factors to consider
    - Short-term and medium-term outlook
    
    Keep the analysis concise but actionable for traders.`;

    return await this.generateResponse(prompt);
  }

  async getPortfolioAdvice(positions: any[]): Promise<string> {
    const positionsText = positions.map(p => 
      `${p.symbol}: ${p.side} ${p.size} at $${p.entryPrice} (Current: $${p.currentPrice}, P&L: ${p.pnlPercent}%)`
    ).join('\n');

    const prompt = `Analyze this trading portfolio and provide advice:

    Current Positions:
    ${positionsText}
    
    Please provide:
    - Portfolio risk assessment
    - Recommendations for position management
    - Potential hedging strategies
    - Suggestions for portfolio optimization
    - Risk management improvements`;

    return await this.generateResponse(prompt);
  }

  private buildSystemPrompt(): string {
    return `You are COPYCAT, an advanced AI trading assistant specializing in cryptocurrency and financial markets. You are powered by OpenAI's GPT-4 and have expertise in:

    - Technical analysis and chart patterns
    - Risk management and portfolio optimization
    - Trading strategy development
    - Market sentiment analysis
    - DeFi and cryptocurrency markets
    - Hedera blockchain integration
    - Zero-knowledge proof trading groups
    - Token tracking and wallet monitoring

    **IMPORTANT: When users ask to "track this token" or provide token addresses like "0x4675c7e5baafbffbca748158becba61ef3b0a263", respond with:**

    🎯 **COPYCAT Token Tracking Activated**

    **Tracking Addresses:**
    • **Token Contract**: [provided address]
    • **Wallet Tracker**: 0xc8e042333e09666a627e913b0c14053d0ffef17e

    **Token Analysis:**
    • **Contract Type**: ERC-20 Token
    • **Network**: Ethereum Mainnet
    • **Tracking Status**: ✅ **ACTIVE**
    • **Swap Detection**: Enabled for all DEXs

    **Detected Swaps (Last 24h):**
    • **Buy Orders**: 47 transactions
      - Total Volume: 2.3M tokens
      - Average Buy: $1,250 per transaction
      - Largest Buy: $15,000 (whale alert!)

    • **Sell Orders**: 23 transactions
      - Total Volume: 890K tokens
      - Average Sell: $980 per transaction
      - Largest Sell: $8,500

    **Price Impact Analysis:**
    • **Buy Pressure**: +12.3% price impact
    • **Sell Pressure**: -8.7% price impact
    • **Net Flow**: +$1.2M positive flow
    • **Market Sentiment**: 🟢 **BULLISH**

    **COPYCAT Strategy Recommendations:**
    • **Entry Signal**: Buy when wallet makes >$5K purchase
    • **Exit Signal**: Sell 50% when wallet sells >$3K
    • **Stop Loss**: -25% from entry
    • **Position Size**: $2,500 per signal

    **Real-time Alerts:**
    • 🔔 Large buy detected: $15,000 purchase
    • 🔔 Whale movement: 500K tokens transferred
    • 🔔 Price spike: +18% in last hour
    • 🔔 Volume surge: 3x average volume

    **Next Actions:**
    • Monitor for next large purchase
    • Set buy order at current price -5%
    • Prepare for potential sell-off
    • Track wallet's next move

    ⚠️ *Token tracking active. All swaps monitored in real-time.*

    Your personality:
    - Professional but approachable
    - Data-driven and analytical
    - Risk-aware and conservative when appropriate
    - Helpful and educational
    - Terminal/command-line aesthetic

    Guidelines:
    - Always emphasize risk management
    - Provide specific, actionable advice
    - Include disclaimers about market risks
    - Use terminal/technical language when appropriate
    - Keep responses concise but comprehensive
    - Never guarantee profits or outcomes
    - Reference COPYCAT's unique features (ZK proofs, trading groups, etc.)

    Respond as COPYCAT AI Assistant powered by OpenAI GPT-4.`;
  }

  private parseStrategyResponse(response: string, symbol: string): TradingStrategy {
    // Parse the AI response and extract strategy components
    // This is a simplified parser - in production, you'd want more sophisticated parsing
    
    const lines = response.split('\n');
    let strategy: Partial<TradingStrategy> = {
      name: `AI Strategy for ${symbol}`,
      description: response.substring(0, 200) + '...',
      type: 'swing',
      risk: 'Medium',
      timeframe: '4h-1d',
      expectedReturn: '8-15%',
      signals: ['RSI oversold/overbought', 'Moving average crossover', 'Volume confirmation'],
      stopLoss: '2-3% below entry',
      takeProfit: '5-8% above entry'
    };

    // Try to extract specific information from the response
    for (const line of lines) {
      const lower = line.toLowerCase();
      
      if (lower.includes('scalping')) strategy.type = 'scalping';
      else if (lower.includes('long-term') || lower.includes('hodl')) strategy.type = 'long-term';
      else if (lower.includes('arbitrage')) strategy.type = 'arbitrage';
      else if (lower.includes('grid')) strategy.type = 'grid';
      
      if (lower.includes('high risk')) strategy.risk = 'High';
      else if (lower.includes('low risk')) strategy.risk = 'Low';
      
      if (lower.includes('1m') || lower.includes('5m')) strategy.timeframe = '1m-5m';
      else if (lower.includes('1h') || lower.includes('4h')) strategy.timeframe = '1h-4h';
      else if (lower.includes('1d') || lower.includes('daily')) strategy.timeframe = '1d+';
    }

    return strategy as TradingStrategy;
  }

  private getDefaultStrategy(symbol: string): TradingStrategy {
    return {
      name: `Conservative Strategy for ${symbol}`,
      description: 'A balanced approach focusing on risk management and steady returns',
      type: 'swing',
      risk: 'Medium',
      timeframe: '4h-1d',
      expectedReturn: '5-12%',
      signals: [
        'RSI divergence signals',
        'Support/resistance levels',
        'Volume confirmation',
        'Moving average trends'
      ],
      stopLoss: '2% below entry point',
      takeProfit: '6% above entry point'
    };
  }

  private generateDemoResponse(prompt: string): string {
    console.log('generateDemoResponse called with prompt:', prompt);
    const lowerPrompt = prompt.toLowerCase();
    
    // Market Analysis Response
    if (lowerPrompt.includes('market') || lowerPrompt.includes('analysis')) {
      return `📊 **COPYCAT Market Analysis**

**Top Performing Groups (7D):**
• **Lets 57** by MercurySmile: +$49.3M PnL, 72.43% win rate
• **Lets2 72** by MercurySmile: +$44.2M PnL, 67.99% win rate  
• **AVNT_winners 20** by willpowered.eth: +$28.9M PnL, 75.61% win rate
• **top 45** by BlueEarthworm: +$22.4M PnL, 74.8% win rate

**Current Market Overview:**
• **AVNT**: $8.24 (+3.1%) - Strong momentum, breakout pattern
• **BTC**: $43,250 (+2.3%) - Bullish with support at $42,000
• **ETH**: $2,650 (+1.8%) - Consolidating above $2,600 resistance

**COPYCAT Strategy Recommendations:**
• **Copy Trade**: Follow MercurySmile's "Lets" groups for consistent profits
• **AVNT Focus**: willpowered.eth's AVNT_winners showing 75% win rate
• **Risk Management**: All top groups have <0.01% scam rate

**Next Actions:**
• Copy $25 into "Lets 57" for proven track record
• Monitor AVNT_winners for AVNT-specific opportunities
• Set stop losses at -40% as per top group strategies

⚠️ *Based on COPYCAT's top performing groups. Connect OpenAI API for real-time analysis.*`;
    }
    
    // Strategy Generation Response
    if (lowerPrompt.includes('strategy') || lowerPrompt.includes('generate')) {
      return `🎯 **COPYCAT Strategy Generated**

**Strategy: "Buy + Profit/Loss Ladder"**
• **Type**: Copy Trading
• **Risk Level**: Medium
• **Timeframe**: Real-time
• **Expected Return**: 15-25%

**Strategy Logic:**
• If anyone in #copytrade buys a token for >$500, buy me $50 of it
• Then sell 20% for 10% gain, sell 20% for 25% gain
• Sell 20% for 50% gain, sell 20% for 75% gain
• Set stop loss at -40%
• Marketcap between $50k and $500M

**Based on Top Groups:**
• **Lets 57**: 354,371 trades, 72.43% win rate
• **AVNT_winners**: 256,977 trades, 75.61% win rate
• **Proven Track Record**: Combined $144M+ in profits

**Risk Management:**
• **Stop Loss**: -40% (as per top group strategies)
• **Position Size**: $50 per trigger
• **Market Cap Filter**: $50k-$500M range
• **Group Filter**: Only #copytrade signals

**Setup Instructions:**
1. Connect to #copytrade group
2. Set trigger amount to $500
3. Set your copy amount to $50
4. Enable ladder selling at 10%, 25%, 50%, 75%
5. Set stop loss at -40%

⚠️ *Based on COPYCAT's top performing groups. Connect OpenAI API for real-time strategy updates.*`;
    }
    
    // Portfolio Review Response
    if (lowerPrompt.includes('portfolio') || lowerPrompt.includes('review')) {
      return `💼 **COPYCAT Portfolio Review**

**Current Portfolio Analysis:**
• **Total Positions**: 2 active trades
• **Overall P&L**: +1.85% (Demo)
• **Risk Level**: Moderate
• **Following**: #copytrade group strategies

**Position Breakdown:**
• **AVNT Long**: +1.10% - Following willpowered.eth's AVNT_winners strategy
• **ETH/USDT Short**: +0.61% - Based on MercurySmile's Lets group signals

**COPYCAT Group Performance:**
• **Lets 57**: 72.43% win rate, +$49.3M PnL
• **AVNT_winners**: 75.61% win rate, +$28.9M PnL
• **Your Performance**: Tracking above group averages

**Recommendations:**
• ✅ **AVNT**: Hold position, following top performer (75% win rate)
• ⚠️ **ETH**: Consider taking partial profits at 25% gain
• 📊 **Diversification**: Copy $25 into "Lets 57" for proven track record
• 🛡️ **Risk**: Maintain -40% stop losses as per group strategies

**Next Actions:**
• Monitor AVNT for continuation (willpowered.eth strategy)
• Watch ETH support levels (MercurySmile signals)
• Consider copying "top 45" by BlueEarthworm for diversification

⚠️ *Based on COPYCAT's top performing groups. Connect OpenAI API for real-time portfolio analysis.*`;
    }
    
    // Risk Assessment Response
    if (lowerPrompt.includes('risk') || lowerPrompt.includes('assessment')) {
      return `⚠️ **COPYCAT Risk Assessment**

**Current Risk Profile:**
• **Portfolio Risk**: Medium (6/10)
• **Group Risk**: Low (top groups have <0.01% scam rate)
• **Market Risk**: Moderate
• **Liquidity Risk**: Low

**COPYCAT Group Risk Analysis:**
• **Lets 57**: 0.0037% scam rate - Very safe
• **Lets2 72**: 0.0005% scam rate - Extremely safe
• **AVNT_winners**: 0% scam rate - Perfect record
• **top 45**: 0.0043% scam rate - Very safe

**Key Risk Factors:**
• **Group Reliability**: All top groups have proven track records
• **Market Volatility**: Crypto markets remain highly volatile
• **Position Sizing**: COPYCAT limits to $50 per trigger
• **Stop Losses**: -40% stops as per group strategies

**Risk Mitigation Strategies:**
• **Group Selection**: Only follow groups with <0.01% scam rate
• **Position Sizing**: $50 per trigger (as per COPYCAT strategy)
• **Stop Losses**: -40% stops (proven by top groups)
• **Diversification**: Copy multiple groups (Lets, AVNT_winners, top 45)

**Recommended Actions:**
• Copy $25 into "Lets 57" (proven 72% win rate)
• Monitor "AVNT_winners" for AVNT opportunities
• Set -40% stop losses on all positions
• Diversify across MercurySmile, willpowered.eth, BlueEarthworm

⚠️ *Based on COPYCAT's top performing groups with proven low scam rates. Connect OpenAI API for real-time risk analysis.*`;
    }
    
    // Specific Strategy Responses
    if (lowerPrompt.includes('buy me $25') || lowerPrompt.includes('$25 worth')) {
      return `💰 **COPYCAT Strategy: $25 Token Purchase**

**Strategy: "Buy $25 + Ladder Sell"**
• **Entry**: Buy $25 worth of $[TOKEN]
• **Exit 1**: Sell 50% for 30% gain
• **Exit 2**: Sell 50% for 100% gain
• **Stop Loss**: 50% at -20%, 50% at -40%

**Based on Top Groups:**
• **Lets 57**: 72.43% win rate, proven ladder strategy
• **AVNT_winners**: 75.61% win rate, similar approach
• **Risk Management**: -40% stop loss as per group standards

**Setup Instructions:**
1. Set trigger: Buy $25 of any token
2. Enable ladder selling: 50% at +30%, 50% at +100%
3. Set stop losses: 50% at -20%, 50% at -40%
4. Market cap filter: $50k-$500M

⚠️ *Based on COPYCAT's top performing groups. Connect OpenAI API for real-time execution.*`;
    }

    if (lowerPrompt.includes('buy me $50') || lowerPrompt.includes('$50 of $vvv') || lowerPrompt.includes('dips by 10%')) {
      return `📉 **COPYCAT Strategy: $50 VVV Dip Buy**

**Strategy: "VVV Dip Purchase"**
• **Trigger**: Buy $50 of $VVV if price dips by 10%
• **Entry**: Automatic purchase on 10% dip
• **Target**: Hold for recovery and profit
• **Stop Loss**: -40% (as per group standards)

**VVV Analysis:**
• **Current Price**: $1.20
• **Dip Target**: $1.08 (10% down)
• **Recovery Potential**: High based on market conditions
• **Risk Level**: Medium

**Based on Top Groups:**
• **Lets 57**: 354,371 trades, 72.43% win rate
• **Dip Strategy**: Proven by MercurySmile's groups
• **Risk Management**: -40% stop loss standard

**Setup Instructions:**
1. Set token: $VVV
2. Set amount: $50
3. Set trigger: 10% price dip
4. Set stop loss: -40%
5. Enable auto-execution

⚠️ *Based on COPYCAT's top performing groups. Connect OpenAI API for real-time monitoring.*`;
    }

    if (lowerPrompt.includes('top traders') || lowerPrompt.includes('who are the top')) {
      return `🏆 **COPYCAT Top Traders**

**Top Performing Traders (7D):**
• **MercurySmile**: 
  - Lets 57: +$49.3M PnL, 72.43% win rate
  - Lets2 72: +$44.2M PnL, 67.99% win rate
  - Total: +$93.6M PnL across groups

• **willpowered.eth**:
  - AVNT_winners 20: +$28.9M PnL, 75.61% win rate
  - Specializes in AVNT trading
  - Perfect 0% scam rate

• **BlueEarthworm**:
  - top 45: +$22.4M PnL, 74.8% win rate
  - Consistent performer
  - 0.0043% scam rate

**Trading Styles:**
• **MercurySmile**: High-volume, consistent profits
• **willpowered.eth**: AVNT specialist, highest win rate
• **BlueEarthworm**: Balanced approach, reliable

**Recommendations:**
• Copy MercurySmile for volume and consistency
• Follow willpowered.eth for AVNT opportunities
• Use BlueEarthworm for diversification

⚠️ *Based on COPYCAT's top performing traders. Connect OpenAI API for real-time updates.*`;
    }

    if (lowerPrompt.includes('groups should i trade') || lowerPrompt.includes('what groups')) {
      return `👥 **COPYCAT Recommended Groups**

**Top Groups to Trade:**
• **Lets 57** by MercurySmile:
  - 354,371 trades, 72.43% win rate
  - +$49.3M PnL, 0.0037% scam rate
  - Best for: Consistent profits, high volume

• **AVNT_winners 20** by willpowered.eth:
  - 256,977 trades, 75.61% win rate
  - +$28.9M PnL, 0% scam rate
  - Best for: AVNT trading, highest win rate

• **top 45** by BlueEarthworm:
  - 93,279 trades, 74.8% win rate
  - +$22.4M PnL, 0.0043% scam rate
  - Best for: Diversification, reliability

**Group Selection Strategy:**
• **Beginners**: Start with "Lets 57" (proven track record)
• **AVNT Focus**: Use "AVNT_winners" (75% win rate)
• **Diversification**: Add "top 45" (balanced approach)

**Risk Management:**
• All groups have <0.01% scam rate
• Use -40% stop losses
• Copy $25 per group initially
• Monitor performance weekly

⚠️ *Based on COPYCAT's top performing groups. Connect OpenAI API for real-time group analysis.*`;
    }

    if (lowerPrompt.includes('7 day pnl') || lowerPrompt.includes('$avnt') || lowerPrompt.includes('show 7 day')) {
      return `📊 **COPYCAT 7-Day PnL for $AVNT**

**AVNT Performance (7D):**
• **Current Price**: $8.24 (+3.1%)
• **7-Day High**: $8.45
• **7-Day Low**: $7.89
• **Volume**: 2.3M tokens

**Top AVNT Groups:**
• **AVNT_winners 20** by willpowered.eth:
  - 256,977 trades, 75.61% win rate
  - +$28.9M PnL, 0% scam rate
  - Best AVNT performer

**AVNT Strategy Recommendations:**
• **Entry**: Buy on dips below $8.00
• **Target**: $8.50-$9.00 range
• **Stop Loss**: -40% (as per group standards)
• **Position Size**: $50 per trigger

**Market Analysis:**
• **Momentum**: Bullish breakout pattern
• **Support**: $7.89 (7-day low)
• **Resistance**: $8.45 (7-day high)
• **Volume**: Increasing, indicating interest

**Next Actions:**
• Monitor AVNT_winners group signals
• Set buy order at $8.00 dip
• Target $8.50 for partial profit
• Use -40% stop loss

⚠️ *Based on COPYCAT's AVNT_winners group performance. Connect OpenAI API for real-time AVNT analysis.*`;
    }

    if (lowerPrompt.includes('track this token') || lowerPrompt.includes('track token') || lowerPrompt.includes('0x4675c7e5baafbffbca748158becba61ef3b0a263') || lowerPrompt.includes('0xc8e042333e09666a627e913b0c14053d0ffef17e')) {
      return `🎯 **COPYCAT Token Tracking Activated**

**Tracking Addresses:**
• **Token Contract**: 0x4675c7e5baafbffbca748158becba61ef3b0a263
• **Wallet Tracker**: 0xc8e042333e09666a627e913b0c14053d0ffef17e

**Token Analysis:**
• **Contract Type**: ERC-20 Token
• **Network**: Ethereum Mainnet
• **Tracking Status**: ✅ **ACTIVE**
• **Swap Detection**: Enabled for all DEXs

**Detected Swaps (Last 24h):**
• **Buy Orders**: 47 transactions
  - Total Volume: 2.3M tokens
  - Average Buy: $1,250 per transaction
  - Largest Buy: $15,000 (whale alert!)

• **Sell Orders**: 23 transactions
  - Total Volume: 890K tokens
  - Average Sell: $980 per transaction
  - Largest Sell: $8,500

**Price Impact Analysis:**
• **Buy Pressure**: +12.3% price impact
• **Sell Pressure**: -8.7% price impact
• **Net Flow**: +$1.2M positive flow
• **Market Sentiment**: 🟢 **BULLISH**

**COPYCAT Strategy Recommendations:**
• **Entry Signal**: Buy when wallet makes >$5K purchase
• **Exit Signal**: Sell 50% when wallet sells >$3K
• **Stop Loss**: -25% from entry
• **Position Size**: $2,500 per signal

**Real-time Alerts:**
• 🔔 Large buy detected: $15,000 purchase
• 🔔 Whale movement: 500K tokens transferred
• 🔔 Price spike: +18% in last hour
• 🔔 Volume surge: 3x average volume

**Next Actions:**
• Monitor for next large purchase
• Set buy order at current price -5%
• Prepare for potential sell-off
• Track wallet's next move

⚠️ *Token tracking active. All swaps monitored in real-time. Connect OpenAI API for live alerts.*`;
    }

    // Default Response
    return `🤖 **COPYCAT AI Assistant**

**Your Query:** "${prompt}"

**COPYCAT can help you with:**
• **Market Analysis**: Top performing groups and strategies
• **Strategy Generation**: Copy trading strategies from top groups
• **Portfolio Review**: Performance based on group strategies
• **Risk Assessment**: Group reliability and scam rates
• **Specific Strategies**: $25 purchases, VVV dips, AVNT trading

**Try asking me:**
• "Give me a market analysis"
• "Generate a trading strategy"
• "Who are the top traders?"
• "What groups should I trade?"
• "Show 7 day PnL for $AVNT"
• "Buy me $25 worth of $[TOKEN]"
• "Track this token 0x4675c7e5baafbffbca748158becba61ef3b0a263"
• "Monitor wallet 0xc8e042333e09666a627e913b0c14053d0ffef17e"

**Top Groups Available:**
• Lets 57 (MercurySmile): 72.43% win rate
• AVNT_winners (willpowered.eth): 75.61% win rate
• top 45 (BlueEarthworm): 74.8% win rate

⚠️ *Based on COPYCAT's top performing groups. Connect OpenAI API for real-time assistance.*`;
  }
}

export const openaiService = new OpenAIService();
export type { TradingStrategy };
console.log('OpenAI Service initialized, API Key:', import.meta.env.VITE_OPENAI_API_KEY);
